import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { FleetVehicle } from '@/lib/api/fleet-api'
import {
    detectDepartures,
    motionStatusOf,
    nextInRotation,
    selectMoving,
} from '@/lib/fleet-spotlight'

export const ROTATE_MS = 20_000
export const PIN_TIMEOUT_MS = 60_000

export interface FleetSpotlight {
    moving: FleetVehicle[]
    spotlight: FleetVehicle | null
    pinnedId: string | null
    departedIds: Set<string>
    togglePin: (id: string) => void
}

// Chữ ký nội dung fleet: id + trạng thái di chuyển của từng xe. Dùng làm khoá
// effect/memo để re-render không liên quan (đồng hồ, poll khác) không kích lại
// logic hay reset đồng hồ xoay vòng — `vehicles` là mảng mới mỗi lần render.
function fleetSignature(vehicles: FleetVehicle[]): string {
    return vehicles.map((v) => `${v.vehicle_id}:${motionStatusOf(v)}`).join('|')
}

// Pool xoay spotlight. Có xe đang chạy → chỉ xoay trong nhóm xe chạy. Không xe
// nào chạy → xoay toàn bộ xe (kể cả offline) để xe rời tầm phủ sóng rồi online
// lại không biến mất khỏi spotlight. Sort theo id để thứ tự xoay ổn định giữa
// các lần poll (mảng vehicles có thể đổi thứ tự).
function rotationPoolIds(vehicles: FleetVehicle[]): string[] {
    const running = selectMoving(vehicles)
    const source = running.length > 0 ? running : vehicles
    return source.map((v) => v.vehicle_id).sort()
}

// Chọn xe cho ô spotlight màn máy trạm. Luôn có spotlight khi còn xe: có xe chạy
// thì xoay giữa các xe chạy, không có thì xoay toàn bộ. Đang spotlight xe không
// chạy mà xuất hiện xe chạy → nhảy sang xe chạy ngay (pool thu về nhóm xe chạy,
// xe cũ rớt pool nên không chờ hết ROTATE_MS). Chạm để ghim.
export function useFleetSpotlight(vehicles: FleetVehicle[]): FleetSpotlight {
    const prevMotion = useRef<Map<string, number>>(new Map())
    const [spotlightId, setSpotlightId] = useState<string | null>(null)
    const [pinnedId, setPinnedId] = useState<string | null>(null)
    const [departedIds, setDepartedIds] = useState<Set<string>>(new Set())
    const pinTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const signature = fleetSignature(vehicles)

    // Xe đang chạy (giữ thứ tự API). Chỉ tính lại khi nội dung fleet đổi → tham
    // chiếu ổn định giữa các render khác. Dùng cho đèn LED + đếm ở panel.
    const moving = useMemo(() => selectMoving(vehicles), [signature])

    const poolIds = useMemo(() => rotationPoolIds(vehicles), [signature])
    const poolKey = poolIds.join('|')

    // Mỗi khi nội dung fleet đổi: cập nhật departure (chỉ để nhấn nháy hàng vừa
    // khởi hành trong danh sách) và giữ spotlight luôn hợp lệ trong pool hiện
    // tại. Xe chạy vừa xuất hiện làm pool thu về nhóm xe chạy → spotlight cũ (xe
    // không chạy) rớt pool → nhảy sang xe chạy ngay, không chờ hết ROTATE_MS.
    useEffect(() => {
        const departures = detectDepartures(prevMotion.current, vehicles)
        const nextMotion = new Map<string, number>()
        for (const v of vehicles) {
            nextMotion.set(v.vehicle_id, motionStatusOf(v))
        }
        prevMotion.current = nextMotion

        setDepartedIds((prev) =>
            prev.size === 0 && departures.length === 0
                ? prev
                : new Set(departures),
        )

        // Ghim chỉ giữ khi xe còn trong fleet. Xe đã ghim biến mất (bị xoá/lọc)
        // thì bỏ ghim để rotation chạy lại, tránh spotlight null → panel báo trống
        // dù vẫn còn xe.
        const present = new Set(vehicles.map((v) => v.vehicle_id))
        const pinnedGone = pinnedId !== null && !present.has(pinnedId)
        if (pinnedGone) {
            if (pinTimer.current) clearTimeout(pinTimer.current)
            setPinnedId(null)
        }

        setSpotlightId((cur) => {
            if (pinnedId && !pinnedGone) return pinnedId
            if (cur && poolIds.includes(cur)) return cur
            return poolIds[0] ?? null
        })
    }, [signature, pinnedId, poolIds])

    // Tự xoay vòng khi không ghim và pool có > 1 xe. Khoá theo poolKey (chuỗi) nên
    // render không liên quan không reset interval.
    useEffect(() => {
        if (pinnedId) return
        const ids = poolKey ? poolKey.split('|') : []
        if (ids.length <= 1) return
        const timer = setInterval(() => {
            setSpotlightId((cur) => nextInRotation(ids, cur))
        }, ROTATE_MS)
        return () => clearInterval(timer)
    }, [poolKey, pinnedId])

    const togglePin = useCallback(
        (id: string) => {
            if (pinTimer.current) clearTimeout(pinTimer.current)
            if (pinnedId === id) {
                setPinnedId(null)
                return
            }
            setPinnedId(id)
            setSpotlightId(id)
            pinTimer.current = setTimeout(
                () => setPinnedId(null),
                PIN_TIMEOUT_MS,
            )
        },
        [pinnedId],
    )

    useEffect(
        () => () => {
            if (pinTimer.current) clearTimeout(pinTimer.current)
        },
        [],
    )

    const spotlight = useMemo(
        () =>
            spotlightId
                ? (vehicles.find((v) => v.vehicle_id === spotlightId) ?? null)
                : null,
        [spotlightId, signature],
    )

    return { moving, spotlight, pinnedId, departedIds, togglePin }
}
